// WsTryItPanel — the Try-It view rendered for WEBSOCKET endpoints (replaces the HTTP
// request/response cards). Picks the tier by adapter:
//   - volcengine-asr (or any adapter with a registered playground) → offers a 档2 playground
//     tab AND the 档1 console tab (raw fallback / debugging).
//   - everything else / text protocol → 档1 generic console only.
//
// The tab bar only appears when a playground exists for the adapter; otherwise the console
// renders directly with no chrome.

import React from 'react';
import { XIcons } from '../../primitives.jsx';
import WsConsole from './ws-console.jsx';
import WsPlaygroundAsr from './ws-playground-asr.jsx';
import WsPlaygroundRealtime from './ws-playground-realtime.jsx';
import WsPlaygroundLive from './ws-playground-live.jsx';
import WsPlaygroundDoubao from './ws-playground-doubao.jsx';
import WsPlaygroundDoubaoTts from './ws-playground-doubao-tts.jsx';
import WsPlaygroundDoubaoAst from './ws-playground-doubao-ast.jsx';
import WsPlaygroundDoubaoPodcast from './ws-playground-doubao-podcast.jsx';
import { getWsAdapterClient } from './ws-adapter-client.js';

// adapter name → 档2 playground component. Grows as new playgrounds land.
const PLAYGROUNDS = {
  'volcengine-asr': WsPlaygroundAsr,
  'openai-realtime': WsPlaygroundRealtime,
  'openai-live': WsPlaygroundLive,
  'doubao-realtime': WsPlaygroundDoubao,
  'doubao-tts': WsPlaygroundDoubaoTts,
  'doubao-ast': WsPlaygroundDoubaoAst,
  'doubao-podcast': WsPlaygroundDoubaoPodcast,
};

export default function WsTryItPanel({ api, endpoint, adapters }) {
  const wsConfig = (endpoint && endpoint._raw && endpoint._raw.wsConfig) || {};
  const adapterName = wsConfig.adapter || '';
  const Playground = PLAYGROUNDS[adapterName] || null;
  // adapters: [{ name, label, clientProtocol, ... }] from GET /ws/adapters — used for the
  // adapter chip / protocol hint. Falls back gracefully when unavailable.
  const adapterMeta = Array.isArray(adapters) ? adapters.find((a) => a.name === adapterName) : null;
  const clientProtocol = (adapterMeta && adapterMeta.clientProtocol)
    || (getWsAdapterClient(adapterName) ? getWsAdapterClient(adapterName).clientProtocol : 'text');

  const [tab, setTab] = React.useState(Playground ? 'playground' : 'console');

  const hasUpstream = !!(wsConfig.upstreamUrl && wsConfig.adapter);

  return (
    <div className="col gap-3" style={{ minWidth: 0 }}>
      {/* Adapter meta strip */}
      <div className="card" style={{ padding: '10px 14px' }}>
        <div className="row gap-2" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="tag mono" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <XIcons.IconZap size={11} /> WebSocket
          </span>
          {adapterName && (
            <span className="mono" style={{ fontSize: 12, color: 'var(--fg-2)' }}>
              {adapterMeta ? (adapterMeta.label || adapterName) : adapterName}
            </span>
          )}
          <span className="mono" style={{ fontSize: 11, color: 'var(--fg-4)' }}>· {clientProtocol}</span>
          {!hasUpstream && (
            <span className="mono" style={{ fontSize: 11, color: 'var(--warn)', marginLeft: 'auto' }}>
              upstream not configured
            </span>
          )}
        </div>
      </div>

      {/* Tier tabs (only when a playground exists) */}
      {Playground && (
        <div className="row gap-1" style={{ borderBottom: '1px solid var(--line)' }}>
          {[{ k: 'playground', l: 'Playground' }, { k: 'console', l: 'Console' }].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className="btn btn-sm btn-ghost"
              style={{
                borderRadius: 0, borderBottom: tab === t.k ? '2px solid var(--fg-0)' : '2px solid transparent',
                color: tab === t.k ? 'var(--fg-1)' : 'var(--fg-3)', fontWeight: tab === t.k ? 600 : 400,
              }}>
              {t.l}
            </button>
          ))}
        </div>
      )}

      {Playground && tab === 'playground'
        ? <Playground api={api} endpoint={endpoint} adapterMeta={adapterMeta} />
        : <WsConsole api={api} endpoint={endpoint} />}
    </div>
  );
}
